import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Truck, ArrowRight, EnvelopeSimple, LockKey } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [forgotMode, setForgotMode] = useState(false);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur de connexion");
    } finally {
      setSubmitting(false);
    }
  };

  const onForgot = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("Si l'adresse est connue, un email vous a été envoyé");
      setForgotMode(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center paper-bg relative overflow-hidden" data-testid="login-page">
      {/* Decorative gold rule top */}
      <div className="absolute top-0 left-0 right-0 ny-divider-thick" />
      <div className="absolute bottom-0 left-0 right-0 ny-divider-thick" />

      <div className="w-full max-w-md mx-4">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo-ny-trans.png"
            alt="NY Trans"
            className="h-32 w-auto mb-2"
            style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.08))" }}
          />
          <div className="ny-divider w-32 my-3" />
          <div className="label-tag">Contrôle de Flotte</div>
        </div>

        <div className="bg-white border border-black brutalist-shadow p-6 relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-[var(--ny-gold)]" />
          {!forgotMode ? (
            <>
              <h1 className="serif-display text-3xl mb-1">Connexion</h1>
              <p className="text-sm text-zinc-500 mb-5">
                Accès réservé. Contactez l&apos;administrateur pour obtenir vos identifiants.
              </p>
              <form onSubmit={onLogin} className="space-y-3" data-testid="login-form">
                <div>
                  <Label className="label-tag">Email</Label>
                  <div className="relative">
                    <EnvelopeSimple
                      size={14}
                      weight="bold"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ny-gold-deep)]"
                    />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="vous@entreprise.fr"
                      className="rounded-none border-black pl-9"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-tag">Mot de passe</Label>
                  <div className="relative">
                    <LockKey
                      size={14}
                      weight="bold"
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ny-gold-deep)]"
                    />
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="rounded-none border-black pl-9 mono"
                      required
                      data-testid="login-password"
                    />
                  </div>
                </div>
                {error && (
                  <div
                    className="bg-red-50 border border-red-600 text-red-700 px-3 py-2 text-sm"
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-black text-white py-3 text-xs uppercase tracking-widest font-bold hover:bg-[var(--ny-gold-deep)] disabled:opacity-50 inline-flex items-center justify-center gap-2 border border-black"
                  data-testid="login-submit"
                >
                  {submitting ? "Connexion…" : "Se connecter"}
                  <ArrowRight size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setForgotMode(true);
                    setError("");
                  }}
                  className="w-full text-xs uppercase tracking-widest text-[var(--ny-gold-deep)] hover:text-black mt-2"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="serif-display text-3xl mb-1">Réinitialiser</h1>
              <p className="text-sm text-zinc-500 mb-5">
                Entrez votre email pour recevoir un lien de réinitialisation.
              </p>
              <form onSubmit={onForgot} className="space-y-3">
                <div>
                  <Label className="label-tag">Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@entreprise.fr"
                    className="rounded-none border-black"
                    required
                    data-testid="forgot-email"
                  />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-600 text-red-700 px-3 py-2 text-sm">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-black text-white py-3 text-xs uppercase tracking-widest font-bold hover:bg-[var(--ny-gold-deep)] disabled:opacity-50"
                  data-testid="forgot-submit"
                >
                  {submitting ? "Envoi…" : "Envoyer le lien"}
                </button>
                <button
                  type="button"
                  onClick={() => setForgotMode(false)}
                  className="w-full text-xs uppercase tracking-widest text-zinc-600 hover:text-black mt-2"
                >
                  Retour à la connexion
                </button>
              </form>
            </>
          )}
        </div>
        <div className="text-center text-[10px] uppercase tracking-widest text-zinc-500 mt-6">
          NY Trans · Gestion de flotte · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
